import React from "react";
import { ShieldCheck, Users, Award, GitCompareArrows } from "lucide-react";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { GIL_COLLECTOR_PROFILE } from "@/data/collectorProfilePreview";
import { AlbumStatusPill, AlbumProgressBar } from "@/components/collector/AlbumCardBits";

/**
 * CollectorProfilePreview
 * ────────────────────────
 * A reusable visual mockup that renders a collector's profile from a single
 * data object. Used on the Home page and Register page. Not wired to any
 * backend data — this is a marketing/vision preview.
 *
 * Props:
 *   data              — collector profile object (see
 *                       /app/frontend/src/data/collectorProfilePreview.js)
 *                       Defaults to the approved Gil Maakaroun example.
 *   showTradeRow      — show the "trade matches will appear here" teaser row
 *                       inside the mockup (default true).
 *
 * Do NOT modify the visual design without alignment with the owner.
 */
export default function CollectorProfilePreview({
  data = GIL_COLLECTOR_PROFILE,
  showTradeRow = true,
}) {
  const settings = useSiteSettings();
  const avatarUrl = (settings[data.avatarSettingKey] || "").trim();

  const statCells = [
    { label: "Certificates", value: data.stats.certificates },
    { label: "Albums",       value: data.stats.albums },
    { label: "Completed",    value: data.stats.completed },
    { label: "In Progress",  value: data.stats.inProgress },
  ];

  return (
    <div
      className="relative rounded-3xl border-4 border-[var(--ink)] bg-white shadow-[10px_10px_0_var(--panini-red)] overflow-hidden"
      data-testid="collector-profile-preview"
    >
      {/* Corner labels — concept-preview messaging */}
      <div className="absolute top-3 left-3 z-10 rounded-full bg-[var(--ink)] text-white px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest">
        Collector Profile Preview
      </div>
      <div className="absolute top-3 right-3 z-10 rounded-full bg-[var(--panini-yellow)] text-[var(--ink)] border border-[var(--ink)] px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest">
        Collector Network In Development
      </div>

      {/* Header band with avatar */}
      <div className="bg-gradient-to-br from-[var(--panini-yellow)] via-[var(--panini-yellow-soft)] to-white pt-12 md:pt-10 p-5 md:p-6 border-b-2 border-[var(--ink)]">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl border-2 border-[var(--ink)] bg-white overflow-hidden flex items-center justify-center shrink-0">
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt={data.name}
                className="w-full h-full object-cover object-center"
                data-testid="collector-profile-avatar"
              />
            ) : (
              <Users size={26} className="text-[var(--panini-red)]" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-[var(--smoke)]">Verified Collector</div>
            <div className="font-display text-lg md:text-xl leading-tight truncate" data-testid="collector-profile-name">
              {data.name}
            </div>
            {data.verified && (
              <div className="flex items-center gap-1.5 mt-1 text-xs text-[var(--field-green)] font-bold">
                <ShieldCheck size={12} /> VERIFIED COLLECTOR
              </div>
            )}
          </div>
          <div className="text-right shrink-0">
            <div className="text-[10px] uppercase tracking-widest text-[var(--smoke)]">Level</div>
            <div className="font-mono text-2xl md:text-3xl text-[var(--panini-red)] leading-none" data-testid="collector-profile-level">
              {data.level}
            </div>
          </div>
        </div>

        {/* Summary stats */}
        <div className="grid grid-cols-4 gap-2 mt-5" data-testid="collector-profile-stats">
          {statCells.map((s) => (
            <div key={s.label} className="rounded-lg border border-[var(--ink)]/15 bg-white px-2 py-2 text-center">
              <div className="font-mono text-[var(--ink)] text-lg leading-tight">{s.value}</div>
              <div className="text-[9px] uppercase tracking-widest text-[var(--smoke)] mt-0.5 leading-tight">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Album collection — Phase 2 redesign: large title, cover-design subtitle,
          static nickname, status pill, progress bar with percentage. */}
      <div className="p-5 md:p-6 space-y-4 bg-white">
        <div className="text-[10px] uppercase tracking-widest text-[var(--smoke)]">
          Album Collection · {data.albums.length} Album{data.albums.length === 1 ? "" : "s"}
        </div>
        {data.albums.map((a) => {
          const percent = a.status === "completed" ? 100 : Number(a.percent) || 0;
          const status = a.status === "completed"
            ? "completed"
            : (a.status === "album_only" ? "album_only" : "in_progress");
          const previewAlbum = {
            collection_method: status === "album_only" ? "album_only" : undefined,
            status: status === "completed" ? "completed" : "collecting",
            completion: { completion_percentage: percent, is_complete: percent >= 100 },
          };
          const nickname = (a.nickname || "").trim();
          const subtitle = (a.cover_design || a.subtitle || "").trim();
          return (
            <article
              key={a.name}
              className="rounded-2xl border-2 border-[var(--ink)]/15 bg-white p-3 md:p-4 shadow-[2px_2px_0_var(--ink)]/10"
              data-testid={`collector-profile-album-${a.name.replace(/\s+/g, '-').toLowerCase()}`}
            >
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="font-display text-xl md:text-2xl leading-tight text-[var(--ink)]">
                    {a.name}
                  </div>
                  {subtitle && (
                    <div className="mt-0.5 text-xs text-[var(--smoke)] truncate"
                         data-testid={`collector-profile-album-subtitle-${a.name.replace(/\s+/g, '-').toLowerCase()}`}>
                      {subtitle}
                    </div>
                  )}
                  {nickname && (
                    <div className="mt-1 text-xs italic text-[var(--smoke)]">
                      &ldquo;{nickname}&rdquo;
                    </div>
                  )}
                </div>
                <AlbumStatusPill status={status}
                  testid={`collector-profile-album-status-${a.name.replace(/\s+/g, '-').toLowerCase()}`} />
              </div>
              <div className="mt-2">
                <AlbumProgressBar album={previewAlbum}
                  testid={`collector-profile-album-progress-${a.name.replace(/\s+/g, '-').toLowerCase()}`} />
              </div>
            </article>
          );
        })}

        {/* Missing / duplicate totals */}
        <div className="grid grid-cols-2 gap-3 pt-3 border-t border-[var(--ink)]/10">
          <div className="rounded-xl bg-[var(--panini-yellow-soft)]/50 border border-[var(--ink)]/10 px-3 py-2.5" data-testid="collector-profile-missing-total">
            <div className="text-[10px] uppercase tracking-widest text-[var(--smoke)]">Total Missing Stickers</div>
            <div className="font-mono text-xl">{data.totals.missingStickers}</div>
          </div>
          <div className="rounded-xl bg-[var(--field-green)]/10 border border-[var(--ink)]/10 px-3 py-2.5" data-testid="collector-profile-duplicates-total">
            <div className="text-[10px] uppercase tracking-widest text-[var(--smoke)]">Total Duplicates</div>
            <div className="font-mono text-xl">{data.totals.duplicates}</div>
          </div>
        </div>
        <p className="text-[11px] text-[var(--smoke)] leading-relaxed">
          Totals represent this collector&apos;s <strong>entire World Cup collection</strong> across every tournament — not a single album.
        </p>

        {/* Badges */}
        {data.badges?.length > 0 && (
          <div className="pt-3 border-t border-[var(--ink)]/10">
            <div className="text-[10px] uppercase tracking-widest text-[var(--smoke)] mb-2">Collector Badges</div>
            <div className="flex flex-wrap gap-2">
              {data.badges.map((b) => (
                <span key={b} className="inline-flex items-center gap-1.5 rounded-full border border-[var(--panini-red)]/30 bg-[var(--panini-red)]/5 px-2.5 py-1 text-[10px] font-bold">
                  <Award size={11} className="text-[var(--panini-red)]" /> {b}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Trade recommendations */}
        {data.tradeRecommendations?.length > 0 && (
          <div className="pt-3 border-t border-[var(--ink)]/10" data-testid="collector-profile-trade-recs">
            <div className="text-[10px] uppercase tracking-widest text-[var(--smoke)] mb-2">
              Trade Recommendations
            </div>
            <ul className="space-y-1.5">
              {data.tradeRecommendations.map((line) => (
                <li key={line} className="flex items-start gap-2 text-xs text-[var(--ink)]/80">
                  <GitCompareArrows size={12} className="mt-0.5 flex-shrink-0 text-[var(--field-green)]" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {showTradeRow && (
          <div className="mt-3 rounded-xl border-2 border-dashed border-[var(--field-green)]/60 bg-[var(--field-green)]/5 px-3 py-2.5 flex items-center gap-2">
            <GitCompareArrows size={14} className="text-[var(--field-green)] flex-shrink-0" />
            <span className="text-xs text-[var(--ink)]/80">Trade matches will appear here.</span>
            <span className="ml-auto text-[9px] uppercase tracking-widest text-[var(--smoke)]">Future</span>
          </div>
        )}

        <p className="text-[10px] uppercase tracking-widest text-[var(--smoke)] pt-1">
          Concept preview only. Not connected to backend data.
        </p>
      </div>
    </div>
  );
}
